import React, { useState, useEffect } from "react";
import "./AdminProtectedRoute.css";
import { getApiUrl } from "../utils/api";

const AdminProtectedRoute = ({ children }) => {
  const [token, setToken] = useState(localStorage.getItem("adminToken") || "");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [logoutSuccessNotice, setLogoutSuccessNotice] = useState("");
  const [welcomeNotice, setWelcomeNotice] = useState("");

  useEffect(() => {
    // Check if user recently logged out
    const logoutMsg = sessionStorage.getItem("adminLogoutNotify");
    if (logoutMsg) {
      setLogoutSuccessNotice("Logged out Successfully");
      sessionStorage.removeItem("adminLogoutNotify");
      const timer = setTimeout(() => setLogoutSuccessNotice(""), 4000);
      return () => clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    // Check if existing token is valid via GET /api/admin/auth/me
    if (token) {
      fetch(getApiUrl("/admin/auth/me"), {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })
        .then((res) => res.json())
        .then((data) => {
          if (!data.success) {
            // Token expired or invalid
            localStorage.removeItem("adminToken");
            localStorage.removeItem("adminUser");
            setToken("");
          }
        })
        .catch(() => {
          // Keep token if offline or handle gracefully
        });
    }
  }, [token]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password.trim()) {
      setError("Please enter both username and password");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await fetch(getApiUrl("/admin/auth/login"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          username: username.trim(),
          password: password.trim()
        })
      });

      const data = await response.json();

      if (data.success && data.token) {
        localStorage.setItem("adminToken", data.token);
        localStorage.setItem("adminUser", JSON.stringify(data.admin));

        const adminName = data.admin?.name || "Admin";
        setWelcomeNotice(`Welcome back, ${adminName}!`);

        setToken(data.token);
        setError("");

        // Auto hide welcome notice after 4 seconds
        setTimeout(() => {
          setWelcomeNotice("");
        }, 4000);
      } else {
        setError(data.message || "Invalid credentials. Please try again.");
      }
    } catch (err) {
      console.error("Login request failed:", err);
      setError("Server error or connection failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="admin-login-container">
        {/* LOGOUT SUCCESS GLASSMORPHISM TOAST */}
        {logoutSuccessNotice && (
          <div className="glass-toast toast-logout-success">
            <span className="toast-icon">👋</span>
            <span className="toast-text">{logoutSuccessNotice}</span>
          </div>
        )}

        <div className="admin-login-box">
          <div className="admin-login-header">
            <div className="admin-avatar-icon">👑</div>
            <h2>Admin Portal</h2>
            <p className="admin-login-sub">Secure JWT Authentication</p>
          </div>

          {error && <div className="admin-error-badge">⚠️ {error}</div>}

          <form onSubmit={handleLogin}>
            <div className="input-group">
              <label>Username / Email</label>
              <input
                type="text"
                placeholder="Enter Username or Email"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                autoComplete="username"
              />
            </div>

            <div className="input-group">
              <label>Password</label>
              <input
                type="password"
                placeholder="Enter Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
              />
            </div>

            <button type="submit" className="login-submit-btn" disabled={loading}>
              {loading ? "Authenticating..." : "Login to Admin Dashboard 🚀"}
            </button>
          </form>

          <p className="admin-login-footer">
            🔒 Protected by JWT & bcrypt Security
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* WELCOME BACK GLASSMORPHISM TOAST */}
      {welcomeNotice && (
        <div className="glass-toast toast-welcome-success">
          <span className="toast-icon">✨</span>
          <span className="toast-text">Welcome back, Admin!</span>
        </div>
      )}
      {children}
    </>
  );
};

export default AdminProtectedRoute;